"use client"

import { deleteLineItem, updateLineItem } from "@lib/data/cart"
import { HttpTypes } from "@medusajs/types"
import AmzPrice from "@modules/amz/components/price"
import ErrorMessage from "@modules/checkout/components/error-message"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Spinner from "@modules/common/icons/spinner"
import Image from "next/image"
import { useState } from "react"

const AmzCartItem = ({
  item,
  currencyCode,
}: {
  item: HttpTypes.StoreCartLineItem
  currencyCode: string
}) => {
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    setUpdating(true)
    await fn()
      .catch((err) => setError(err.message))
      .finally(() => setUpdating(false))
  }

  const variantTitle = item.variant?.title ?? item.variant_title
  const options = variantTitle && variantTitle !== "Estandar" ? [variantTitle] : []

  return (
    <li className="flex gap-x-4 py-4 border-b border-[#ddd]" data-testid="product-row">
      <LocalizedClientLink
        href={`/products/${item.product_handle}`}
        className="relative w-24 h-24 small:w-44 small:h-44 shrink-0 bg-[#f7f7f7]"
      >
        {item.thumbnail && (
          <Image src={item.thumbnail} alt="" fill sizes="180px" className="object-cover" />
        )}
      </LocalizedClientLink>
      <div className="flex-1 min-w-0 flex flex-col gap-y-1">
        <div className="flex justify-between gap-x-4">
          <LocalizedClientLink
            href={`/products/${item.product_handle}`}
            className="text-base small:text-lg leading-6 line-clamp-2 hover:text-amz-link-hover"
            data-testid="product-title"
          >
            {item.product_title}
          </LocalizedClientLink>
          <span className="hidden small:block shrink-0 font-bold">
            <AmzPrice amount={item.total ?? 0} currencyCode={currencyCode} size="sm" />
          </span>
        </div>
        <span className="text-xs text-amz-green">En stock</span>
        <span className="text-xs text-[#565959]">Envio gestionado con Packlink PRO</span>
        {options.length > 0 && (
          <span className="text-xs" data-testid="product-variant">
            {options.join(" · ")}
          </span>
        )}
        <span className="small:hidden font-bold">
          <AmzPrice amount={item.total ?? 0} currencyCode={currencyCode} size="sm" />
        </span>
        <div className="flex items-center gap-x-3 mt-2 text-xs">
          <select
            value={item.quantity}
            onChange={(e) => run(() => updateLineItem({ lineId: item.id, quantity: Number(e.target.value) }))}
            className="bg-[#f0f2f2] border border-[#d5d9d9] rounded-lg px-2 py-1 shadow-sm"
            disabled={updating}
            data-testid="product-select-button"
            aria-label="Cantidad"
          >
            {Array.from({ length: Math.max(10, item.quantity) }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                Cant.: {n}
              </option>
            ))}
          </select>
          <span className="text-[#ddd]">|</span>
          <button
            className="amz-link"
            onClick={() => run(() => deleteLineItem(item.id))}
            disabled={updating}
            data-testid="product-delete-button"
          >
            Eliminar
          </button>
          {updating && <Spinner />}
        </div>
        <ErrorMessage error={error} data-testid="product-error-message" />
      </div>
    </li>
  )
}

export default AmzCartItem
